import { motion } from "framer-motion";
import { Phone, Gift } from "lucide-react";
import { PHONE_TEL } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";
import { AmbientMedia } from "@/components/spa/AmbientMedia";
import { TiltCard } from "@/components/spa/TiltCard";

export const GiftCertificates = () => {
  const { t } = useLang();
  return (
    <section id="gifts" data-testid="gifts-section" className="relative py-24 lg:py-40 bg-[#0c100e] overflow-hidden">
      <span aria-hidden="true" className="ghost-char bottom-4 right-2">禮</span>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative pb-10"
        >
          <div className="overflow-visible">
            <TiltCard
              testid="gifts-media"
              max={4}
              lift={5}
              glow="0 26px 65px -20px rgba(197,160,89,0.55)"
              className="w-full overflow-hidden border border-[#c5a059]/25"
            >
              <motion.div
                initial={{ scale: 1.12 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-[420px] sm:h-[520px]"
              >
                <AmbientMedia
                  video="/gift/certificate.mp4"
                  poster="/gift/certificate.jpg"
                  alt="Elite Spa gift certificate beside glowing candles"
                  className="w-full h-full object-cover block"
                />
              </motion.div>
            </TiltCard>
          </div>
          <div
            data-testid="gifts-badge"
            className="absolute left-6 sm:left-10 bottom-0 translate-y-1/2 bg-[#c5a059] text-[#050706] px-6 py-3.5 flex items-center gap-3 shadow-[0_16px_40px_-12px_rgba(197,160,89,0.55)]"
          >
            <Gift size={16} />
            <span className="text-[11px] tracking-[0.28em] uppercase font-medium whitespace-nowrap">{t.gifts.badge}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[#c5a059]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{t.gifts.label}</span>
          </div>
          <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2]">
            {t.gifts.title.pre}<span className="italic text-[#c5a059]">{t.gifts.title.it}</span>{t.gifts.title.post}
          </h2>
          <p className="mt-8 text-[#8a9690] font-light leading-relaxed max-w-lg">{t.gifts.body}</p>
          <div className="mt-10 space-y-0">
            {t.gifts.perks.map((p, i) => (
              <div key={i} className="flex items-center gap-5 py-4 border-t border-white/10 last:border-b">
                <span className="font-serif-display italic text-[#c5a059]">0{i + 1}</span>
                <span className="text-sm text-[#f0f4f2]/80 font-light">{p}</span>
              </div>
            ))}
          </div>
          <a
            href={PHONE_TEL}
            data-testid="gifts-call-button"
            className="mt-10 inline-flex items-center gap-3 bg-[#c5a059] text-[#050706] hover:bg-[#dcc286] px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300"
          >
            <Phone size={14} /> {t.gifts.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
